import { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  collection,
  query,
  addDoc,
  orderBy,
  onSnapshot,
  serverTimestamp,
  Timestamp,
  deleteDoc,
  doc,
} from "firebase/firestore";
import { db } from "../../../firebase";
import styles from "../../../styles/AdminConsole.module.css";
import { TEAM_CATEGORIES } from "../../../data/constants";
import StatCard from "../shared/components/StatCard";
import SelectWrapper from "../shared/components/SelectWrapper";
import CategoryBadge from "../shared/components/CategoryBadge";

// ── Constants ────────────────────────────────────────────────────────────────
const CURRENCY_OPTIONS = [
  { code: "USD", label: "USD — US Dollar", rate: 83.5 },
  { code: "EUR", label: "EUR — Euro", rate: 90.2 },
  { code: "GBP", label: "GBP — British Pound", rate: 105.8 },
  { code: "AED", label: "AED — UAE Dirham", rate: 22.7 },
  { code: "JPY", label: "JPY — Japanese Yen", rate: 0.56 },
];

const INITIAL_ORDER_FORM = {
  orderNumber: "",
  orderMonth: "",
  orderValueUSD: "",
  orderValueCurrency: "USD",
  orderValueINR: "",
  incoterm: "",
  skuCount: "",
  skuBreakdown: "",
  exFactory: "",
  portRoute: "",
  orderStart: "",
  totalDays: "",
  deliveryDate: "",
  fabricDefault: "",
  totalQuantity: "",
  buyerName: "",
  buyerLocation: "",
  supRole: "Supplier",
  supName: "",
  supLocation: "",
  supDeliveryDate: "",
  supPaymentMethod: "",
  supPaymentTerms: "",
  supPaymentBank: "",
  supExFactory: "",
  supProducts: "",
  supSelectedProduct: "",
};

function formatINR(inr) {
  if (!inr || isNaN(inr) || inr <= 0) return "";
  const crore = 1_00_00_000,
    lakh = 1_00_000,
    thou = 1_000;
  if (inr >= crore) return (inr / crore).toFixed(2) + " Cr";
  if (inr >= lakh) return (inr / lakh).toFixed(2) + " L";
  if (inr >= thou) return (inr / thou).toFixed(2) + " K";
  return Math.round(inr).toString();
}
function formatUSD(usd) {
  if (!usd || isNaN(usd) || usd <= 0) return "—";
  if (usd >= 1_000_000_000) return `$${(usd / 1_000_000_000).toFixed(2)}B`;
  if (usd >= 1_000_000) return `$${(usd / 1_000_000).toFixed(2)}M`;
  if (usd >= 1_000) return `$${(usd / 1_000).toFixed(1)}K`;
  return `$${Math.round(usd)}`;
}

function getStatusLabel(order) {
  if (order?.cancelled) return "cancelled";
  if (order?.archived) return "archived";
  return "active";
}

// ── Component ────────────────────────────────────────────────────────────────
export default function OrdersTab({ users, showToast }) {
  const navigate = useNavigate();

  // ── Own Firebase subscription — only mounts when this tab is active ───────
  const [allOrders, setAllOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(true);
  const [deleteConfirm, setDeleteConfirm] = useState(null); // order key "ownerUid:id"

  const supplierUsers = useMemo(
    () => users.filter((u) => !TEAM_CATEGORIES.includes(u.category)),
    [users],
  );

  useEffect(() => {
    if (users.length === 0) {
      setAllOrders([]);
      setLoadingOrders(false);
      return;
    }
    const unsubs = supplierUsers.map((u) => {
      const q = query(
        collection(db, "users", u.uid, "orders"),
        orderBy("createdAt", "desc"),
      );
      return onSnapshot(q, (snap) => {
        setAllOrders((prev) => {
          const rest = prev.filter((o) => o._ownerUid !== u.uid);
          const fresh = snap.docs.map((d) => ({
            id: d.id,
            _ownerUid: u.uid,
            _ownerName: u.name,
            ...d.data(),
          }));
          return [...rest, ...fresh];
        });
        setLoadingOrders(false);
      });
    });
    if (supplierUsers.length === 0) setLoadingOrders(false);
    return () => unsubs.forEach((fn) => fn());
  }, [users, supplierUsers]);

  // ── Order Profile form ────────────────────────────────────────────────────
  const [op, setOp] = useState(INITIAL_ORDER_FORM);
  const [opSupplierQuery, setOpSupplierQuery] = useState("");
  const [opSaving, setOpSaving] = useState(false);
  const setOpField = useCallback(
    (k, v) => setOp((prev) => ({ ...prev, [k]: v })),
    [],
  );

  // ── Derived stats — using boolean flags, not status string ────────────────
  const stats = useMemo(
    () => ({
      total: allOrders.filter((o) => o.orderNumber?.trim()).length,
      active: allOrders.filter(
        (o) => o.orderNumber?.trim() && !o.cancelled && !o.archived,
      ).length,
      completed: allOrders.filter((o) => o.orderNumber?.trim() && !!o.archived)
        .length,
      cancelled: allOrders.filter((o) => o.orderNumber?.trim() && !!o.cancelled)
        .length,
      valueUSD: allOrders.reduce(
        (sum, o) => sum + (Number(o.orderValueUSD) || 0),
        0,
      ),
      buyers: new Set(allOrders.map((o) => o.Buyer?.name).filter(Boolean)).size,
    }),
    [allOrders],
  );

  const sortedOrders = useMemo(
    () =>
      allOrders
        .filter((o) => o.orderNumber?.trim())
        .sort(
          (a, b) =>
            (b.createdAt?.toMillis?.() ?? 0) - (a.createdAt?.toMillis?.() ?? 0),
        ),
    [allOrders],
  );

  // ── Delete handler ────────────────────────────────────────────────────────
  const handleDeleteOrder = useCallback(
    async (order) => {
      const key = `${order._ownerUid}:${order.id}`;
      if (deleteConfirm !== key) {
        setDeleteConfirm(key);
        return;
      }
      try {
        await deleteDoc(doc(db, "users", order._ownerUid, "orders", order.id));
        setDeleteConfirm(null);
        showToast(`Order "${order.orderNumber}" deleted.`, "error");
      } catch (err) {
        showToast(err.message, "error");
      }
    },
    [deleteConfirm, showToast],
  );

  // ── Save order profile ────────────────────────────────────────────────────
  const handleSaveOrderProfile = useCallback(async () => {
    const queryStr = opSupplierQuery.trim();
    if (!queryStr) {
      showToast("Enter a supplier name or UID.", "error");
      return;
    }
    const match = users.find(
      (u) =>
        (u.name || "").toLowerCase() === queryStr.toLowerCase() ||
        u.uid === queryStr,
    );
    if (!match) {
      showToast(`No supplier found matching "${queryStr}".`, "error");
      return;
    }
    setOpSaving(true);
    try {
      const toTs = (s) => (s ? Timestamp.fromDate(new Date(s)) : null);
      const toArr = (s) =>
        s
          .split(",")
          .map((x) => x.trim())
          .filter(Boolean);
      const fabric = op.fabricDefault.trim();
      await addDoc(collection(db, "users", match.uid, "orders"), {
        orderNumber: op.orderNumber.trim(),
        orderMonth: op.orderMonth.trim(),
        orderValueUSD: Number(op.orderValueUSD) || 0,
        orderValueCurrency: op.orderValueCurrency || "USD",
        orderValueINR: op.orderValueINR.trim(),
        incoterm: op.incoterm.trim(),
        skuCount: Number(op.skuCount) || 0,
        skuBreakdown: toArr(op.skuBreakdown),
        exFactory: op.exFactory.trim(),
        portRoute: op.portRoute.trim(),
        orderStart: toTs(op.orderStart),
        totalDays: Number(op.totalDays) || 0,
        deliveryDate: toTs(op.deliveryDate),
        fabricDefault: fabric,
        totalQuantity: op.totalQuantity.trim(),
        construction: fabric ? fabric.toLowerCase() : "",
        Buyer: { name: op.buyerName.trim(), location: op.buyerLocation.trim() },
        Suppliers: [
          {
            role: op.supRole.trim() || "Supplier",
            name: op.supName.trim(),
            location: op.supLocation.trim(),
            deliveryDate: toTs(op.supDeliveryDate),
            paymentMethod: op.supPaymentMethod.trim(),
            paymentTerms: op.supPaymentTerms.trim(),
            paymentBank: op.supPaymentBank.trim(),
            exFactory: op.supExFactory.trim(),
            Products: toArr(op.supProducts),
            selectedProduct: op.supSelectedProduct.trim(),
          },
        ],
        sharedOrder: !!match.sharedOrder,
        sharedWith: match.sharedWith ?? null,
        createdAt: serverTimestamp(),
        status: "active",
      });
      showToast(`Order profile saved for "${match.name}".`);
    } catch (err) {
      showToast(err.message, "error");
    } finally {
      setOpSaving(false);
    }
  }, [op, opSupplierQuery, users, showToast]);

  // ── Reusable field renderer ───────────────────────────────────────────────
  const renderOpField = useCallback(
    (label, key, type = "text", placeholder = "") => {
      const isAutoCalc = key === "totalDays";
      return (
        <div className={styles.opField} key={key}>
          <label className={styles.opFieldLabel}>{label}</label>
          <input
            className={`${styles.input}${type === "number" || isAutoCalc ? ` ${styles.inputNumber}` : ""}${isAutoCalc ? ` ${styles.inputReadonly}` : ""}`}
            type={type}
            placeholder={placeholder ? `Eg: ${placeholder}` : ""}
            value={op[key]}
            readOnly={isAutoCalc}
            tabIndex={isAutoCalc ? -1 : undefined}
            onChange={
              isAutoCalc
                ? undefined
                : (e) => {
                    const val = e.target.value;
                    setOpField(key, val);
                    if (key === "orderStart" || key === "deliveryDate") {
                      const start = key === "orderStart" ? val : op.orderStart;
                      const end =
                        key === "deliveryDate" ? val : op.deliveryDate;
                      if (start && end) {
                        const diff = Math.round(
                          (new Date(end) - new Date(start)) /
                            (1000 * 60 * 60 * 24),
                        );
                        setOpField("totalDays", diff > 0 ? String(diff) : "");
                      } else {
                        setOpField("totalDays", "");
                      }
                    }
                    if (key === "exFactory" || key === "deliveryDate") {
                      const ef = key === "exFactory" ? val : op.exFactory;
                      const del =
                        key === "deliveryDate" ? val : op.deliveryDate;
                      if (ef && del && new Date(ef) >= new Date(del))
                        showToast(
                          "Ex-Factory date must be before Delivery Date.",
                          "error",
                        );
                    }
                  }
            }
          />
        </div>
      );
    },
    [op, setOpField, showToast],
  );

  return (
    <>
      {/* ── Overview ── */}
      <h2 className={styles.sectionTitle}>Order Overview</h2>
      <div className={styles.statsRow}>
        <StatCard
          label="Total Orders"
          value={stats.total}
          sub="all order profiles created"
        />
        <StatCard
          label="Active"
          value={stats.active}
          sub="currently in production"
        />
        <StatCard
          label="Total Value"
          value={formatUSD(stats.valueUSD)}
          sub="combined order value (USD)"
        />
        <StatCard
          label="Unique Buyers"
          value={stats.buyers || "—"}
          sub="distinct buyer names"
        />
        <StatCard
          label="Completed"
          value={stats.completed}
          sub="archived orders fulfilled"
        />
        <StatCard
          label="Cancelled"
          value={stats.cancelled}
          sub="orders cancelled"
        />
      </div>

      <div className={styles.rule} />

      {/* ── All Orders Table ── */}
      <h2 className={styles.sectionTitle}>All Orders</h2>
      {loadingOrders ? (
        <p className={styles.stateText}>Loading orders…</p>
      ) : sortedOrders.length === 0 ? (
        <p className={styles.stateText}>No orders found.</p>
      ) : (
        <div className={styles.tableWrapper} style={{ maxWidth: "100%" }}>
          <div className={styles.tableScroll}>
            <table className={styles.table}>
              <thead>
                <tr className={styles.tableHeadRow}>
                  <th className={styles.th}>#</th>
                  <th className={styles.th}>Order #</th>
                  <th className={styles.th}>Supplier</th>
                  <th className={styles.th}>Buyer</th>
                  <th className={styles.th}>Value (USD)</th>
                  <th className={styles.th}>Ex-Factory</th>
                  <th className={styles.th}>Delivery</th>
                  <th className={styles.th}>Status</th>
                  <th className={styles.th}>Delete</th>
                </tr>
              </thead>
              <tbody>
                {sortedOrders.map((order, index) => {
                  const key = `${order._ownerUid}:${order.id}`;
                  const isPendingDelete = deleteConfirm === key;
                  const statusLabel = getStatusLabel(order);

                  return (
                    <tr
                      key={key}
                      className={styles.tableRow}
                      style={
                        order.cancelled
                          ? { opacity: 0.5 }
                          : order.archived
                            ? { opacity: 0.7 }
                            : {}
                      }
                    >
                      <td className={styles.td} style={{ opacity: 0.5 }}>
                        {index + 1}
                      </td>
                      <td className={styles.td} style={{ fontWeight: 600 }}>
                        {order.orderNumber || "—"}
                      </td>
                      <td className={styles.td}>
                        <button
                          className={styles.supplierLink}
                          onClick={() => navigate(`/plm/${order._ownerUid}`)}
                        >
                          {order._ownerName}
                        </button>
                      </td>
                      <td className={styles.td}>{order.Buyer?.name || "—"}</td>
                      <td className={styles.td}>
                        {order.orderValueUSD
                          ? `$${Number(order.orderValueUSD).toLocaleString()}`
                          : "—"}
                      </td>
                      <td className={styles.td}>{order.exFactory || "—"}</td>
                      <td className={styles.td}>
                        {order.deliveryDate
                          ? (order.deliveryDate
                              .toDate?.()
                              .toLocaleDateString("en-GB") ??
                            order.deliveryDate)
                          : "—"}
                      </td>
                      <td className={styles.td}>
                        <CategoryBadge category={statusLabel} />
                      </td>
                      <td className={styles.td}>
                        {isPendingDelete ? (
                          <div style={{ display: "flex", gap: 4 }}>
                            <button
                              className={`${styles.btn} ${styles.btnPrimary}`}
                              style={{
                                padding: "4px 10px",
                                fontSize: 9,
                                marginTop: 0,
                                background: "#c84040",
                                borderColor: "#c84040",
                              }}
                              onClick={() => handleDeleteOrder(order)}
                            >
                              Confirm
                            </button>
                            <button
                              className={styles.btn}
                              style={{ padding: "4px 10px", fontSize: 9 }}
                              onClick={() => setDeleteConfirm(null)}
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <button
                            className={styles.editBtn}
                            style={{ color: "#e08080" }}
                            onClick={() => handleDeleteOrder(order)}
                            title="Delete this order"
                          >
                            ✕
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className={styles.rule} />

      {/* ── Order Profile Form ── */}
      <h2 className={styles.sectionTitle}>Order Profile</h2>
      <div className={styles.orderProfileSection}>
        <div className={styles.orderProfileCard}>
          <div className={styles.statLabel}>Set Order Profile</div>

          <div className={styles.opSupplierRow}>
            <div className={styles.opField} style={{ flex: "1 1 320px" }}>
              <label className={styles.opFieldLabel}>
                Supplier (name or UID)
              </label>
              <input
                className={styles.input}
                placeholder="Eg: Ravi Textiles or paste UID"
                value={opSupplierQuery}
                onChange={(e) => setOpSupplierQuery(e.target.value)}
              />
            </div>
          </div>

          <div className={styles.opSubhead}>Order Details</div>
          <div className={styles.opGrid}>
            {renderOpField(
              "Order Number",
              "orderNumber",
              "text",
              "PO-2026-001",
            )}
            {renderOpField("Order Month", "orderMonth", "text", "June 2026")}
            {renderOpField("Incoterm", "incoterm", "text", "FOB")}

            {/* Currency + Value */}
            <div className={styles.opField}>
              <label className={styles.opFieldLabel}>Order Value</label>
              <div className={styles.currencyRow}>
                <div
                  className={`${styles.selectWrapper} ${styles.currencySelect}`}
                >
                  <select
                    className={styles.input}
                    value={op.orderValueCurrency}
                    onChange={(e) => {
                      const code = e.target.value;
                      setOpField("orderValueCurrency", code);
                      const raw = parseFloat(op.orderValueUSD);
                      if (!isNaN(raw) && raw > 0) {
                        const cur = CURRENCY_OPTIONS.find(
                          (c) => c.code === code,
                        );
                        setOpField(
                          "orderValueINR",
                          formatINR(raw * (cur?.rate ?? 1)),
                        );
                      }
                    }}
                  >
                    {CURRENCY_OPTIONS.map((c) => (
                      <option key={c.code} value={c.code}>
                        {c.code}
                      </option>
                    ))}
                  </select>
                  <span className={styles.selectChevron}>▾</span>
                </div>
                <input
                  className={`${styles.input} ${styles.inputNumber} ${styles.currencyAmount}`}
                  type="number"
                  placeholder="Eg: 500000"
                  value={op.orderValueUSD}
                  onChange={(e) => {
                    const val = e.target.value;
                    setOpField("orderValueUSD", val);
                    const raw = parseFloat(val);
                    if (!isNaN(raw) && raw > 0) {
                      const cur = CURRENCY_OPTIONS.find(
                        (c) => c.code === op.orderValueCurrency,
                      );
                      setOpField(
                        "orderValueINR",
                        formatINR(raw * (cur?.rate ?? 1)),
                      );
                    } else {
                      setOpField("orderValueINR", "");
                    }
                  }}
                />
              </div>
            </div>

            <div className={styles.opField}>
              <label className={styles.opFieldLabel}>Order Value (INR)</label>
              <input
                className={`${styles.input} ${styles.inputNumber} ${styles.inputReadonly}`}
                type="text"
                readOnly
                tabIndex={-1}
                placeholder="Auto-calculated"
                value={op.orderValueINR ? `₹ ${op.orderValueINR}` : ""}
              />
            </div>

            {renderOpField(
              "Total Order Quantity (units)",
              "totalQuantity",
              "number",
              "8000 units",
            )}
            {renderOpField("SKU Count", "skuCount", "number", "12")}
            {renderOpField(
              "SKU Breakdown (comma-sep.)",
              "skuBreakdown",
              "text",
              "4 styles, 3 colorways,S-XL",
            )}
            {renderOpField("Ex-Factory Date", "exFactory", "date")}
            {renderOpField(
              "Port / Route",
              "portRoute",
              "text",
              "Nhava Sheva → Hamburg",
            )}
            {renderOpField("Order Start", "orderStart", "date")}
            {renderOpField("Delivery Date", "deliveryDate", "date")}
            <div className={styles.opField}>
              <label className={styles.opFieldLabel}>Total Days</label>
              <input
                className={`${styles.input} ${styles.inputNumber} ${styles.inputReadonly}`}
                type="number"
                readOnly
                tabIndex={-1}
                placeholder="Auto-calculated"
                value={op.totalDays}
              />
            </div>
            <div className={styles.opField}>
              <label className={styles.opFieldLabel}>
                Construction / Fabric
              </label>
              <div className={styles.selectWrapper}>
                <select
                  className={styles.input}
                  value={op.fabricDefault}
                  onChange={(e) => setOpField("fabricDefault", e.target.value)}
                >
                  <option value="">— Select —</option>
                  <option value="Woven">Woven</option>
                  <option value="Knitted">Knitted</option>
                  <option value="Non-woven">Non-woven</option>
                </select>
                <span className={styles.selectChevron}>▾</span>
              </div>
            </div>
          </div>

          <div className={styles.opSubhead}>Buyer</div>
          <div className={styles.opGrid}>
            {renderOpField(
              "Buyer Name",
              "buyerName",
              "text",
              "Marks & Spencer",
            )}
            {renderOpField(
              "Buyer Location",
              "buyerLocation",
              "text",
              "London, United Kingdom",
            )}
          </div>

          <div className={styles.opSubhead}>Supplier — Party &amp; Payment</div>
          <div className={styles.opGrid}>
            {renderOpField("Role", "supRole", "text", "Supplier")}
            {renderOpField(
              "Supplier Name",
              "supName",
              "text",
              "Pacific Exports",
            )}
            {renderOpField(
              "Supplier Location",
              "supLocation",
              "text",
              "Karur,India",
            )}
            {renderOpField("Supplier Delivery Date", "supDeliveryDate", "date")}
            {renderOpField(
              "Payment Method",
              "supPaymentMethod",
              "text",
              "LC at Sight",
            )}
            {renderOpField(
              "Payment Terms",
              "supPaymentTerms",
              "text",
              "30 Advance 70 LC",
            )}
            {renderOpField(
              "Payment Bank",
              "supPaymentBank",
              "text",
              "HDFC Bank",
            )}
            {renderOpField("Supplier Ex-Factory", "supExFactory", "date")}
          </div>

          <div className={styles.opSubhead}>Products</div>
          <div className={styles.opGrid}>
            {renderOpField(
              "Products (comma-separated)",
              "supProducts",
              "text",
              "T-shirts, Pants",
            )}
            {renderOpField(
              "Selected Product",
              "supSelectedProduct",
              "text",
              "T-shirts",
            )}
          </div>

          <div className={styles.opActions}>
            <button
              className={`${styles.btn} ${styles.btnPrimary}`}
              onClick={handleSaveOrderProfile}
              disabled={opSaving}
            >
              {opSaving ? "Saving…" : "Save Order Profile"}
            </button>
          </div>
        </div>
      </div>

      <div className={styles.rule} />
    </>
  );
}
